<template>
  <div class="app-container postage-recharge-page">
    <BjDivider>{{ $t('billingPage.PostageRecharge') }}</BjDivider>
    <BjPage
      ref="BjPage"
      :query-params="queryParams"
      :col-span="colSpan"
      :label-width="'100px'"
      :get-action="queryApi"
      :serial="serial"
      serial-fixed="left"
      :selection="selection"
      res="page"
      :reset-data="adaptInvoiceRows"
      :before-reset-search="beforeResetSearch"
      :before-query-search="beforeQuerySearch"
      @handleSelectionChange="handleSelectionChange"
      @showSetting="SortableTable.show"
      @header-dragend="SortableTable.handleHeaderDragend"
      @header-contextmenu="SortableTable.handleHeaderContextmenu"
      @afterFetchOriginalData="afterFetchOriginalData"
    >
      <template v-slot:baseForm>
        <el-col :span="colSpan">
          <el-form-item :label="$t('billingPage.PostageRechargeCarrier')" prop="logisticsCompany">
            <BjSelectLov
              v-model="queryParams.logisticsCompany"
              clearable
              :option-list="companyList"
              :placeholder="$t('billingPage.PostageRechargeAllPartners')"
              @change="handleQuery"
            />
          </el-form-item>
        </el-col>
        <el-col :span="colSpan">
          <el-form-item :label="$t('billingPage.YearWeek')" prop="week">
            <el-date-picker
              ref="weekPicker"
              v-model="weekDataNow"
              style="width: 100%"
              type="week"
              format="yyyy-WW"
              :placeholder="$t('billingPage.yearAndWeeks')"
              @change="changeWeek"
            />
          </el-form-item>
        </el-col>
        <el-col :span="colSpan">
          <el-form-item :label="$t('billingPage.PostageRechargeInvoiceNo')" prop="invoiceNumber">
            <BjInput
              v-model="queryParams.invoiceNumber"
              :maxlength="MAX_LENGTH.DEFAULT"
              :placeholder="$t('billingPage.PostageRechargeSearchInvoice')"
              clearable
              @keyup.enter.native="handleQuery"
            />
          </el-form-item>
        </el-col>
      </template>
      <template v-slot:tableTop>
        <div class="recharge-summary">
          <div v-for="item in summaryCards" :key="item.key" class="recharge-summary__item" :class="item.className">
            <div class="recharge-summary__label">{{ item.label }}</div>
            <div class="recharge-summary__value">{{ item.value }}</div>
            <div class="recharge-summary__desc">{{ item.desc }}</div>
          </div>
        </div>
      </template>
      <template v-slot:tableOperation>
        <el-col>
          <el-button type="primary" class="main-btn" icon="el-icon-plus" :loading="carrierOptionsLoading" @click="showUploadDialog">
            {{ $t('billingPage.PostageRechargeNewInvoice') }}
          </el-button>
        </el-col>
      </template>
      <template v-slot:tableColumn>
        <template v-for="(col, index) in dropCols">
          <vxe-table-column
            v-if="col.prop !== 'bjTableAction'"
            :key="`col_${index}_${col.prop}_${col.width || 'auto'}`"
            :field="col.prop"
            :title="col.label"
            :min-width="col.minWidth || col.width || 120"
            :fixed="col.fixed || false"
            :align="getColumnAlign()"
            class-name="item"
          >
            <template v-slot:header>
              <span class="postage-table-header__label" :title="col.label">{{ col.shortLabel || col.label }}</span>
              <el-tooltip v-if="col.formula" effect="dark" placement="top">
                <div slot="content" class="formula-tooltip">
                  <strong>{{ $t(formulaDefinitions[col.formula].labelKey) }}</strong><br />
                  {{ $t(formulaDefinitions[col.formula].descriptionKey) }}<br /><br />
                  {{ $t(formulaDefinitions[col.formula].formulaKey) }}
                </div>
                <i class="el-icon-info formula-info" tabindex="0" />
              </el-tooltip>
            </template>
            <template slot-scope="scope">
              <template v-if="col.prop === 'invoiceNumber'">
                <span class="link" @click="onView(scope.row)">{{ scope.row.invoiceNumber }}</span>
              </template>
              <template v-else-if="col.prop === 'yearWeek'">
                {{ formatYearWeek(scope.row) }}
              </template>
              <template v-else-if="moneyProps.includes(col.prop)">
                <el-popover
                  v-if="col.prop === 'currentChargeAmount'"
                  placement="bottom-end"
                  width="300"
                  trigger="click"
                  popper-class="postage-calculation-popover"
                >
                  <div class="calculation-breakdown">
                    <div class="calculation-breakdown__title">
                      {{ $t('billingPage.PostageRechargeCalculationBreakdown') }}
                    </div>
                    <p>{{ $t('billingPage.PostageRechargeCalculatedFromOrders') }}</p>
                    <el-button type="text" @click="onView(scope.row)">
                      {{ $t('billingPage.PostageRechargeViewOrderBreakdown') }}
                    </el-button>
                  </div>
                  <span
                    slot="reference"
                    class="semantic-value is-clickable"
                    :class="getCellClass(scope.row, col.prop)"
                  >
                    {{ formatMoney(scope.row[col.prop]) }}
                  </span>
                </el-popover>
                <el-tooltip
                  v-else-if="getValidationError(scope.row, col.prop)"
                  effect="dark"
                  placement="top"
                >
                  <div slot="content">
                    <strong>{{ $t('billingPage.PostageRechargeValidationFailed') }}</strong><br />
                    {{ getValidationMessage(scope.row, col.prop) }}<br />
                    {{ $t('billingPage.PostageRechargeExpected') }}:
                    {{ formatMoney(getValidationError(scope.row, col.prop).expectedValue) }}<br />
                    {{ $t('billingPage.PostageRechargeActual') }}:
                    {{ formatMoney(getValidationError(scope.row, col.prop).actualValue) }}
                  </div>
                  <span
                    class="semantic-value is-invalid"
                    :class="getCellClass(scope.row, col.prop)"
                    @mouseenter="setActiveFormula(scope.row, col.prop)"
                    @mouseleave="clearActiveFormula"
                  >
                    <i class="el-icon-warning-outline" />
                    {{ formatMoney(scope.row[col.prop]) }}
                  </span>
                </el-tooltip>
                <span
                  v-else
                  class="semantic-value"
                  :class="getCellClass(scope.row, col.prop)"
                  @mouseenter="setActiveFormula(scope.row, col.prop)"
                  @mouseleave="clearActiveFormula"
                >
                  {{ formatMoney(scope.row[col.prop]) }}
                </span>
              </template>
              <template v-else-if="col.prop === 'averageProfitMargin'">
                <span class="semantic-value is-margin">
                  {{ formatPercent(scope.row.averageProfitMargin) }}
                </span>
              </template>
              <template v-else-if="col.prop === 'gmtCreate'">
                {{ formatDate(scope.row.gmtCreate) }}
              </template>
              <template v-else>
                {{ scope.row[col.prop] }}
              </template>
            </template>
          </vxe-table-column>
          <vxe-table-column
            v-if="col.prop === 'bjTableAction'"
            :key="`col_${index}_${col.prop}_${col.width || 'auto'}`"
            :field="col.prop"
            :title="col.label"
            :width="Math.max(Number(col.width) || 0, 230)"
            :fixed="col.fixed || 'right'"
            align="center"
          >
            <template slot-scope="scope">
              <el-button type="text" @click="onView(scope.row)">{{ $t('viewText') }}</el-button>
              <el-button type="text" @click="$refs.InvoiceReviewDialog.show(scope.row)">{{ $t('billingPage.postageReview.title') }}</el-button>
              <el-button type="text" class="delete-btn" @click="onDelete(scope.row)">{{ $t('delText') }}</el-button>
            </template>
          </vxe-table-column>
        </template>
      </template>
    </BjPage>
    <UploadInvoiceDialog ref="UploadInvoiceDialog" :company-list="companyList" @refresh="handleQuery" />
    <InvoiceReviewDialog ref="InvoiceReviewDialog" @loaded="onReviewLoaded" />
  </div>
</template>

<script>
import moment from 'moment';
import pageMiXin from '@/utils/page-mixin.js';
import { billApi } from '@/api';
import UploadInvoiceDialog from './components/UploadInvoiceDialog';
import InvoiceReviewDialog from './components/InvoiceReviewDialog';
import {
  POSTAGE_FORMULAS,
  adaptPostageInvoiceRows,
  adaptPostageInvoiceSummary
} from './postage-ui';

export default {
  name: 'BusinessBillingPostageRecharge',
  components: { UploadInvoiceDialog, InvoiceReviewDialog },
  mixins: [pageMiXin],
  data() {
    const currentWeek = moment();
    return {
      selection: false,
      serial: true,
      labelsVersion: 12,
      colSpan: 6,
      queryApi: billApi.getPostageRechargeInvoiceList,
      formulaDefinitions: POSTAGE_FORMULAS,
      activeFormula: null,
      weekDataNow: currentWeek.toDate(),
      queryParams: {
        invoiceNumber: '',
        logisticsCompany: '',
        year: currentWeek.format('GGGG'),
        week: String(currentWeek.isoWeek())
      },
      summary: {
        invoiceAmount: 0,
        matchedAmount: 0,
        unmatchedAmount: 0,
        currentChargeAmount: 0,
        averageProfitMargin: 0,
        pendingUploads: 0
      },
      moneyProps: [
        'invoiceAmount',
        'matchedAmount',
        'unmatchedAmount',
        'costReceived',
        'amountReceived',
        'difference',
        'currentChargeAmount'
      ],
      companyList: [],
      carrierOptionsLoading: false,
      labelList: [
        { label: this.$t('billingPage.PostageRechargeInvoiceNo'), shortLabel: this.$t('billingPage.PostageRechargeListInvoiceNo'), prop: 'invoiceNumber', isCheck: true, disabled: false, width: 125, fixed: 'left' },
        { label: this.$t('billingPage.YearWeek'), shortLabel: this.$t('billingPage.PostageRechargeListYearWeek'), prop: 'yearWeek', isCheck: true, disabled: false, width: 80 },
        { label: this.$t('billingPage.PostageRechargeCarrier'), shortLabel: this.$t('billingPage.PostageRechargeListCarrier'), prop: 'logisticsCompany', isCheck: true, disabled: false, width: 90, fixed: 'left' },
        { label: this.$t('billingPage.PostageRechargeInvoiceAmount'), shortLabel: this.$t('billingPage.PostageRechargeListInvoiceAmount'), prop: 'invoiceAmount', formula: 'invoiceAmount', isCheck: true, disabled: false, width: 110 },
        { label: this.$t('billingPage.PostageRechargeMatchedAmount'), shortLabel: this.$t('billingPage.PostageRechargeListMatchedAmount'), prop: 'matchedAmount', isCheck: true, disabled: false, width: 105 },
        { label: this.$t('billingPage.PostageRechargeUnmatchedAmount'), shortLabel: this.$t('billingPage.PostageRechargeListUnmatchedAmount'), prop: 'unmatchedAmount', isCheck: true, disabled: false, width: 115 },
        { label: this.$t('billingPage.PostageRechargeCostReceived'), shortLabel: this.$t('billingPage.PostageRechargeListCostReceived'), prop: 'costReceived', isCheck: true, disabled: false, width: 105 },
        { label: this.$t('billingPage.PostageRechargeAmountReceived'), shortLabel: this.$t('billingPage.PostageRechargeListAmountReceived'), prop: 'amountReceived', isCheck: true, disabled: false, width: 110 },
        { label: this.$t('billingPage.PostageRechargeDifference'), prop: 'difference', formula: 'difference', isCheck: true, disabled: false, width: 95 },
        { label: this.$t('billingPage.PostageRechargeCurrentChargeAmount'), shortLabel: this.$t('billingPage.PostageRechargeListCurrentChargeAmount'), prop: 'currentChargeAmount', formula: 'listCurrentChargeAmount', isCheck: true, disabled: false, width: 125 },
        { label: this.$t('billingPage.PostageRechargeAverageProfitMargin'), shortLabel: this.$t('billingPage.PostageRechargeListAverageProfitMargin'), prop: 'averageProfitMargin', formula: 'averageProfitMargin', isCheck: true, disabled: false, width: 115 },
        { label: this.$t('billingPage.Operator2'), prop: 'createdBy', isCheck: true, disabled: false, width: 90 },
        { label: this.$t('billingPage.OrderPostagecreate'), shortLabel: this.$t('billingPage.PostageRechargeListCreatedTime'), prop: 'gmtCreate', isCheck: true, disabled: false, width: 125 },
        { label: this.$t('table.operationText'), prop: 'bjTableAction', isCheck: true, disabled: false, width: 230 }
      ]
    };
  },
  computed: {
    summaryCards() {
      return [
        {
          key: 'invoice',
          label: this.$t('billingPage.PostageRechargeInvoiceAmount'),
          value: this.formatMoney(this.summary.invoiceAmount),
          desc: this.$t('billingPage.PostageRechargeInvoiceSummaryDesc'),
          className: 'is-invoice'
        },
        {
          key: 'matched',
          label: this.$t('billingPage.PostageRechargeMatchedAmount'),
          value: this.formatMoney(this.summary.matchedAmount),
          desc: this.$t('billingPage.PostageRechargeMatchedSummaryDesc'),
          className: 'is-matched'
        },
        {
          key: 'unmatched',
          label: this.$t('billingPage.PostageRechargeUnmatchedAmount'),
          value: this.formatMoney(this.summary.unmatchedAmount),
          desc: this.$t('billingPage.PostageRechargeUnmatchedSummaryDesc'),
          className: 'is-unmatched'
        },
        {
          key: 'charge',
          label: this.$t('billingPage.PostageRechargeCurrentChargeAmount'),
          value: this.formatMoney(this.summary.currentChargeAmount),
          desc: this.$t('billingPage.PostageRechargeChargeSummaryDesc'),
          className: 'is-charge'
        },
        {
          key: 'margin',
          label: this.$t('billingPage.PostageRechargeAverageProfitMargin'),
          value: this.formatPercent(this.summary.averageProfitMargin),
          desc: this.$t('billingPage.PostageRechargeMarginSummaryDesc'),
          className: 'is-margin'
        },
        {
          key: 'pending',
          label: this.$t('billingPage.PostageRechargePendingUploads'),
          value: this.summary.pendingUploads,
          desc: this.$t('billingPage.PostageRechargePendingDesc'),
          className: 'is-muted'
        }
      ];
    }
  },
  created() {
    this.loadCarrierOptions();
  },
  methods: {
    onReviewLoaded({ row, currentChargeAmount }) {
      this.$set(row, 'currentChargeAmount', currentChargeAmount);
    },
    async loadCarrierOptions() {
      if (this.carrierOptionsLoading) return false;
      this.carrierOptionsLoading = true;
      try {
        const res = await billApi.getPostageRechargeCarrierOptions();
        if (res.code != 200 || !Array.isArray(res.data) || !res.data.length ||
            res.data.some(value => typeof value !== 'string' || !value.trim())) {
          throw new Error('Invalid carrier options');
        }
        this.companyList = res.data.map(value => ({ label: value, value }));
        return true;
      } catch (error) {
        this.$message.error(this.$t('billingPage.PostageRechargeCarrierOptionsError'));
        return false;
      } finally {
        this.carrierOptionsLoading = false;
      }
    },
    async showUploadDialog() {
      if (!this.companyList.length && !(await this.loadCarrierOptions())) return;
      this.$refs.UploadInvoiceDialog.show();
    },
    setCurrentWeek() {
      const currentWeek = moment();
      this.weekDataNow = currentWeek.toDate();
      this.queryParams.year = currentWeek.format('GGGG');
      this.queryParams.week = String(currentWeek.isoWeek());
    },
    changeWeek() {
      this.$nextTick(() => {
        const displayValue = (this.$refs.weekPicker && this.$refs.weekPicker.displayValue) || '';
        const match = displayValue.match(/^(\d{4})-(\d{1,2})$/);
        this.queryParams.year = match ? match[1] : '';
        this.queryParams.week = match ? String(Number(match[2])) : '';
        this.handleQuery();
      });
    },
    afterFetchOriginalData(res) {
      this.summary = {
        ...this.summary,
        ...adaptPostageInvoiceSummary(res)
      };
    },
    adaptInvoiceRows(rows) {
      return adaptPostageInvoiceRows(rows);
    },
    formatMoney(value) {
      const number = Number(value || 0);
      return `$ ${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatDate(value) {
      if (!value) return '';
      return moment(value).format('DD/MM/YYYY HH:mm');
    },
    formatPercent(value) {
      const number = Number(value || 0);
      return `${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    },
    setActiveFormula(row, prop) {
      const definition = this.formulaDefinitions[prop];
      if (!definition) return;
      this.activeFormula = {
        rowId: row.id,
        resultField: prop,
        fields: definition.fields
      };
    },
    clearActiveFormula() {
      this.activeFormula = null;
    },
    getCellClass(row, prop) {
      const classes = [`is-${prop}`];
      if (
        this.activeFormula &&
        this.activeFormula.rowId === row.id &&
        this.activeFormula.fields.includes(prop)
      ) {
        classes.push('is-formula-linked');
        if (this.activeFormula.resultField === prop) {
          classes.push('is-formula-result');
        }
      }
      return classes;
    },
    getValidationError(row, prop) {
      return (row.validationErrors || []).find(error => error.field === prop);
    },
    getValidationMessage(row, prop) {
      const error = this.getValidationError(row, prop);
      if (!error) return '';
      if (error.code === 'INVOICE_AMOUNT_MISMATCH') {
        return this.$t('billingPage.PostageRechargeInvoiceValidationMessage');
      }
      return error.message;
    },
    getColumnAlign() {
      return 'center';
    },
    formatYearWeek(row) {
      if (!row) return '';
      if (!row.year && !row.week) return '';
      return `${row.year || ''}/${row.week || ''}`;
    },
    onView(row) {
      this.$router.push({
        name: 'BusinessBillingPostageRechargeDetail',
        query: {
          id: row.id,
          invoiceNumber: row.invoiceNumber,
          fileName: row.fileName,
          logisticsCompany: row.logisticsCompany
        }
      });
    },
    onDelete(row) {
      this.$confirm(`${this.$t('toast.continueTipText')}`, this.$t('toast.hintText'), {
        confirmButtonText: this.$t('toast.okText'),
        cancelButtonText: this.$t('toast.cancelText'),
        type: 'warning'
      }).then(() => {
        billApi.deletePostageRechargeInvoice({ id: row.id }).then(res => {
          if (res.code == 200 || !res.failed) {
            this.msgSuccess(this.$t('toast.operationSuccessText'));
            this.handleQuery();
          }
        });
      });
    },
    beforeResetSearch() {
      this.queryParams.invoiceNumber = '';
      this.queryParams.logisticsCompany = '';
      this.setCurrentWeek();
      return new Promise(resolve => {
        resolve();
      });
    },
    beforeQuerySearch(conditionObj) {
      const allowedKeys = ['BjAjaxLoadingHide', 'page', 'pageSize', 'keywords', 'sortedFields', 'year', 'week', 'invoiceNumber', 'logisticsCompany'];
      const params = {};
      allowedKeys.forEach(key => {
        if (conditionObj[key] !== undefined && conditionObj[key] !== null && conditionObj[key] !== '') {
          params[key] = conditionObj[key];
        }
      });
      return Promise.resolve(params);
    }
  }
};
</script>

<style lang="scss" scoped>
.postage-recharge-page {
  --postage-invoice: #2457a6;
  --postage-invoice-bg: #f2f6fc;
  --postage-matched: #237a57;
  --postage-matched-bg: #f0f8f4;
  --postage-unmatched: #a96614;
  --postage-unmatched-bg: #fff8ee;
  --postage-history: #526273;
  --postage-history-bg: #f5f7fa;
  --postage-calculation: #6552a3;
  --postage-calculation-bg: #e8e2f5;
  --postage-charge: #53439a;
  --postage-charge-bg: #dcd3f0;
  --postage-margin: #24756f;
  --postage-margin-bg: #eef8f7;
  --postage-error: #c45656;
  --postage-error-bg: #fef0f0;

  .recharge-summary {
    display: grid;
    grid-template-columns: repeat(6, minmax(150px, 1fr));
    gap: 12px;
    margin: 0 0 14px;
    overflow-x: auto;

    &__item {
      min-height: 104px;
      padding: 15px 16px;
      border-radius: 8px;
      background: #fff;
      border: 1px solid #ebeef5;
      border-top: 3px solid #dcdfe6;

      &.is-invoice {
        border-top-color: var(--postage-invoice);
      }

      &.is-matched {
        border-top-color: var(--postage-matched);
      }

      &.is-unmatched {
        border-top-color: var(--postage-unmatched);
      }

      &.is-charge {
        border-top-color: var(--postage-charge);
      }

      &.is-margin {
        border-top-color: var(--postage-margin);
      }

      &.is-muted {
        border-top-color: #909399;
      }
    }

    &__label {
      color: #606266;
      font-size: 12px;
      font-weight: 600;
      line-height: 18px;
    }

    &__value {
      margin-top: 8px;
      color: #1f2d3d;
      font-size: 22px;
      font-weight: 700;
      line-height: 30px;
      font-variant-numeric: tabular-nums;
    }

    &__desc {
      margin-top: 6px;
      color: #909399;
      font-size: 12px;
      line-height: 18px;
    }
  }

  .link {
    color: #0056d2;
    cursor: pointer;
  }

  .formula-info {
    margin-left: 5px;
    color: #77869a;
    cursor: help;
    outline: none;

    &:focus {
      color: #0056d2;
    }
  }

  .postage-table-header__label {
    white-space: pre-line;
    line-height: 16px;
  }

  .semantic-value {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 92px;
    padding: 3px 7px;
    border: 1px solid transparent;
    border-radius: 4px;
    color: #303133;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    transition: background-color 120ms ease, border-color 120ms ease;

    &.is-invoiceAmount {
      color: var(--postage-invoice);
    }

    &.is-matchedAmount {
      color: var(--postage-matched);
    }

    &.is-unmatchedAmount {
      color: var(--postage-unmatched);
    }

    &.is-costReceived,
    &.is-amountReceived {
      color: var(--postage-history);
    }

    &.is-difference {
      color: var(--postage-calculation);
    }

    &.is-currentChargeAmount {
      color: var(--postage-charge);
    }

    &.is-margin {
      color: var(--postage-margin);
    }

    &.is-clickable {
      cursor: pointer;
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 3px;
    }

    &.is-formula-linked {
      background: var(--postage-calculation-bg);
      border-color: #b7a8dc;
    }

    &.is-formula-result {
      border-color: var(--postage-calculation);
      background: var(--postage-charge-bg);
      box-shadow: 0 0 0 1px rgb(101 82 163 / 18%);
    }

    &.is-invalid {
      border-color: var(--postage-error);
      background: var(--postage-error-bg);
      color: var(--postage-error);
    }
  }

  @media (max-width: 1440px) {
    .recharge-summary {
      grid-template-columns: repeat(3, minmax(190px, 1fr));
    }
  }
}
</style>
